// Row contracts of skill packs (M4-02; 02 §Agents and crews `skill_pack`). Same conventions as
// `rows.ts`: keys are column names, nullability is the column's, enums are the CHECK's; bound to
// `cell.skill_pack` in `contract-bindings.ts`. The manifest and file shapes are the format's
// (`@companyos/domain-agents/skill-packs`, which validates a pack before it is stored); the API
// test parses every core pack's manifest with `SkillPackManifest` so the two cannot drift.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import { SkillAdditionVia, SkillPackFormat, SkillPackScope, SkillPackStatus } from "./enums";
import { Timestamp, Uuid } from "./primitives";

const ident = z.string().max(64);

/** A context layer a pack reads or writes (agent-os.md §12 `crew_context_layer.layer`). */
export const SkillPackLayer = z.enum([
  "manual",
  "identity",
  "business",
  "priorities",
  "voice",
  "connections",
  "decisions",
  "captures",
  "audits",
]);

export const SkillPackSourceRef = z
  .object({
    upstream_url: z.string(),
    upstream_commit: z.string(),
    upstream_path: z.string(),
    license_spdx: z.string(),
    copyright: z.string(),
    trademark_notes: z.string(),
  })
  .meta({ id: "SkillPackSourceRef" });

/** TEMPLATES-T1: a v2 manifest's default access for one connection family. */
export const SkillPackAccessDefault = z
  .object({
    level: z.enum(["R", "A", "D", "M"]),
    actions: z.record(z.string(), z.enum(["R", "A", "D", "M", "never"])).optional(),
    after_probation: z.record(z.string(), z.enum(["A", "D"])).optional(),
    limits: z
      .object({
        daily_acts: z.int().optional(),
        recipient_domains: z.array(z.string()).optional(),
      })
      .optional(),
  })
  .meta({ id: "SkillPackAccessDefault" });

/** `manifest.json`: what CompanyOS adds around the Herk format (version, needs, layers, invocation). */
export const SkillPackManifest = z
  .object({
    format: z.literal("herk-skill"),
    /** 2 (TEMPLATES-T1) adds default access, policy classes, KPI keys, exemplars and tests. */
    format_version: z.union([z.literal(1), z.literal(2)]),
    default_access: z.record(z.string(), SkillPackAccessDefault).optional(),
    policy_classes: z.array(ident).optional(),
    kpi_keys: z.array(ident).optional(),
    exemplars: z.array(z.string()).optional(),
    tests: z.array(z.string()).optional(),
    key: z.string(),
    name: z.string(),
    version: z.string(),
    description: z.string(),
    invocation: z.enum(["model", "user_only"]),
    argument_hint: z.string().nullable(),
    requires_tools: z.array(ident),
    requires_connections: z.array(ident),
    action_kinds: z.array(ident),
    reads_layers: z.array(SkillPackLayer),
    writes_layers: z.array(SkillPackLayer),
    source_ref: SkillPackSourceRef.nullable(),
    license_note: z.string(),
  })
  .meta({ id: "SkillPackManifest" });
export type SkillPackManifest = z.infer<typeof SkillPackManifest>;

/** One file of a pack (text-only): its path, size in bytes, sha256 and content. */
export const SkillPackFile = z
  .object({ path: z.string(), bytes: z.int().min(0), sha256: z.string(), text: z.string() })
  .meta({ id: "SkillPackFile" });

export const SkillPack = z
  .object({
    company_id: Uuid,
    id: Uuid,
    scope: SkillPackScope,
    source_pack_id: Uuid.nullable(),
    key: z.string(),
    name: z.string(),
    description: z.string(),
    version: z.string(),
    format: SkillPackFormat,
    content_hash: z.string().regex(/^[0-9a-f]{64}$/),
    object_key: z.string(),
    manifest: SkillPackManifest,
    source_ref: SkillPackSourceRef.nullable(),
    license_note: z.string(),
    files: z.array(SkillPackFile),
    status: SkillPackStatus,
    published_at: Timestamp,
    deprecated_at: Timestamp.nullable(),
    role_ids: z.array(Uuid),
    /** AGENT-SAFETY: when a run last mounted it; null before the first. */
    last_used_at: Timestamp.nullable(),
    /** AGENT-SAFETY: archived (kept, not mounted) since; null while active. */
    archived_at: Timestamp.nullable(),
    ...ActorShortColumns.shape,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .meta({ id: "SkillPack" });
export type SkillPack = z.infer<typeof SkillPack>;

/**
 * SKILL-OVERRIDES: one version of a company's additions to a skill (`cell.skill_addition`,
 * append-only; the newest is current). `text` null: reset to the default.
 */
export const SkillAddition = z
  .object({
    company_id: Uuid,
    skill_key: z.string(),
    n: z.int().min(1),
    text: z.string().nullable(),
    base_version: z.string(),
    base_hash: z.string().regex(/^[0-9a-f]{64}$/),
    via: SkillAdditionVia,
    ...ActorShortColumns.shape,
    created_at: Timestamp,
  })
  .meta({ id: "SkillAddition" });
export type SkillAddition = z.infer<typeof SkillAddition>;
